import { useUpdateGroupMutation } from '@/app/redux/endpoints/groups';
import {
  useBulkUpdateStudentSettingsMutation,
  useBulkUpdateStudentsMutation,
} from '@/app/redux/endpoints/students';
import { useOrganisation } from '@/app/redux/hooks/organisation';
import { useStudents } from '@/app/redux/hooks/students';
import { useUser } from '@/app/redux/hooks/user';
import { Group } from '@/common/types/group';
import { OrganisationMember } from '@/common/types/organisation';
import {
  AudioDelay,
  ControllerType,
  GameSettings,
} from '@/common/types/student';
import TableInitialsCircle from '@/common/ui/TableInitialsCircle';
import { UNKNOWN_ERROR_MESSAGE } from '@/common/ui/UnknownErrorMessage';
import trackMixpanelEvent from '@/common/utils/mixpanel';
import {
  generateStudentPassword,
  initialGameSettings,
} from '@/common/utils/student';
import LearnerSettings from '@/dashboard/LearnerView/LearnerSettings';
import {
  ArrowCounterClockwiseIcon,
  CaretDownIcon,
  EyeIcon,
  EyeSlashIcon,
  IdentificationCardIcon,
  MagicWandIcon,
  ShuffleIcon,
  UsersThreeIcon,
  WarningCircleIcon,
  XIcon,
} from '@phosphor-icons/react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@shadcn/components/accordion';
import { Alert, AlertDescription } from '@shadcn/components/alert';
import { Button } from '@shadcn/components/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@shadcn/components/dialog';
import { Input } from '@shadcn/components/input';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@shadcn/components/tooltip';
import { chunk, isEqual } from 'lodash';
import { Loader2 } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import styled from 'styled-components';
import { UserMultiSelect } from '../MultiSelect';

interface GroupDetailsProps {
  group: Group;
  managers: OrganisationMember[];
}

export default function GroupDetails({ group, managers }: GroupDetailsProps) {
  const { user } = useUser();
  const { organisation, currentOrganisationId } = useOrganisation();
  const { studentsById } = useStudents();
  const [updateGroup] = useUpdateGroupMutation();
  const [bulkUpdateStudentSettings] = useBulkUpdateStudentSettingsMutation();
  const [bulkUpdateStudents] = useBulkUpdateStudentsMutation();

  const groupStudents = useMemo(
    () =>
      group.studentIds
        .map((id) => studentsById.get(id))
        .filter((s) => s !== undefined),
    [group.studentIds, studentsById],
  );

  const [updatedName, setUpdatedName] = useState(group.name);
  const [isManagerDialogOpen, setManagerDialogOpen] = useState(false);
  const [isManagerDialogLoading, setManagerDialogLoading] = useState(false);
  const [updatedManagerIds, setUpdatedManagerIds] = useState<string[]>(
    group.managerIds,
  );
  const [updatedPassword, setUpdatedPassword] = useState('');
  const [passwordVisible, setPasswordVisible] = useState(true);
  const [isUpdatingPasswords, setIsUpdatingPasswords] = useState(false);

  const initialSettings =
    groupStudents.length > 0 ? groupStudents[0].settings : initialGameSettings;

  const [settings, setSettings] = useState<GameSettings>(initialSettings);
  const [savedSettings, setSavedSettings] =
    useState<GameSettings>(initialSettings);
  const [isSettingsLoading, setIsSettingsLoading] = useState(false);

  const hasSettingsChanged = useMemo(
    () => !isEqual(settings, savedSettings),
    [settings, savedSettings],
  );

  const orgDefaults =
    organisation?.defaultStudentSettings ?? initialGameSettings;
  const canApplyOrgDefaults = !isEqual(settings, orgDefaults);

  useEffect(() => {
    const groupSettings =
      groupStudents.length > 0
        ? groupStudents[0].settings
        : initialGameSettings;
    setUpdatedName(group.name);
    setUpdatedManagerIds(group.managerIds);
    setSettings(groupSettings);
    setSavedSettings(groupSettings);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [group.id]);

  function handleSave() {
    if (updatedName && updatedName !== group.name) {
      updateGroup({
        orgId: currentOrganisationId!,
        groupId: group.id,
        updates: { name: updatedName },
      });
    } else {
      setUpdatedName(group.name);
    }
  }

  async function handleUpdateManagers() {
    setManagerDialogLoading(true);
    try {
      await updateGroup({
        orgId: currentOrganisationId!,
        groupId: group.id,
        updates: { managerIds: updatedManagerIds },
      }).unwrap();
      toast.success('Managers updated');
      trackMixpanelEvent('Group Managers Updated', {
        groupId: group.id,
        managerIds: updatedManagerIds,
      });
    } catch (error) {
      toast.error('Failed to update managers', {
        description: UNKNOWN_ERROR_MESSAGE,
      });
      console.error(error);
    }
    setManagerDialogLoading(false);
  }

  async function handleRemoveManager(managerId: string) {
    try {
      await updateGroup({
        orgId: currentOrganisationId!,
        groupId: group.id,
        updates: {
          managerIds: group.managerIds.filter((id) => id !== managerId),
        },
      }).unwrap();
      toast.success('Manager removed');
    } catch (error) {
      toast.error('Failed to remove manager', {
        description: UNKNOWN_ERROR_MESSAGE,
      });
      console.error(error);
    }
  }

  function handleManagerSelect(userId: string) {
    if (updatedManagerIds.includes(userId)) {
      setUpdatedManagerIds(updatedManagerIds.filter((id) => id !== userId));
    } else {
      setUpdatedManagerIds([...updatedManagerIds, userId]);
    }
  }

  function handlePlayPhonemeChanged(value: boolean) {
    setSettings({ ...settings, playPhonemeOnCardDrop: value });
  }
  function handleControllerTypeChange(value: ControllerType) {
    setSettings({ ...settings, controllerType: value });
  }
  function handleDuelsEnabledChange(value: boolean) {
    setSettings({ ...settings, duelsEnabled: value });
  }
  function handleAudioDelayChange(value: AudioDelay) {
    setSettings({
      ...settings,
      audio: { ...settings.audio, audioDelay: value },
    });
  }
  function handleDistractorCardsChange(value: boolean) {
    setSettings({ ...settings, distractorCardsEnabled: value });
  }
  function handleWordLimitChange(value: number) {
    setSettings({ ...settings, wordLimit: value });
  }
  function handleWordSegmentationChange(value: boolean) {
    setSettings({
      ...settings,
      wordSegmentationEnabled: value,
      audio: {
        ...settings.audio,
        audioDelay: value ? settings.audio.audioDelay : AudioDelay.SHORT,
      },
    });
  }
  function handleArticulatoryGesturesChange(value: boolean) {
    setSettings({ ...settings, articulatoryGesturesEnabled: value });
  }
  function handleApplyOrgDefaults() {
    setSettings(orgDefaults);
  }

  async function handleSaveSettings() {
    if (!organisation) {
      console.error('Missing organisation');
      toast.error('Failed to save settings', {
        description: UNKNOWN_ERROR_MESSAGE,
      });
      return;
    }

    const studentIds = groupStudents.map((s) => s.id);
    const chunks = chunk(studentIds, 50);
    let applied = 0;

    setIsSettingsLoading(true);
    const toastId = toast.loading('Applying settings...', {
      description: "Please don't refresh or leave the page",
    });

    try {
      for (const idChunk of chunks) {
        await bulkUpdateStudentSettings({
          studentIds: idChunk,
          userName: user?.name ?? 'Educator',
          organisationId: organisation.id,
          settings,
          location: 'group-settings',
        });
        applied += idChunk.length;
        toast.loading(`Applying settings (${applied}/${studentIds.length})`, {
          id: toastId,
        });
      }

      toast.success('Settings applied', {
        id: toastId,
        description: '',
      });
      setSavedSettings(settings);
    } catch (e) {
      console.error('Error applying settings:', e);
      if (applied > 0) {
        toast.error('Failed to apply settings', {
          id: toastId,
          description: `Applied to ${applied} of ${studentIds.length} learners. Please retry.`,
        });
      } else {
        toast.error('Failed to apply settings', {
          id: toastId,
          description: UNKNOWN_ERROR_MESSAGE,
        });
      }
    } finally {
      setIsSettingsLoading(false);
    }
  }

  function handleFilterManagers(searchTerm: string) {
    if (!organisation) return [];
    return organisation.members.filter((member) => {
      const key = `${member.name} ${member.email}`;
      const terms = searchTerm.split(' ');
      return terms.every((term) =>
        key.toLowerCase().includes(term.toLowerCase()),
      );
    });
  }

  async function handleUpdatePasswords() {
    if (!organisation) {
      console.error('Missing org');
      toast.error('Failed to update passwords', {
        description: UNKNOWN_ERROR_MESSAGE,
      });
      return;
    }

    const allUpdates = groupStudents.map((s) => ({
      studentId: s.id,
      password: updatedPassword,
    }));
    const chunks = chunk(allUpdates, 50);
    let updated = 0;

    setIsUpdatingPasswords(true);
    const toastId = toast.loading('Updating passwords...');

    try {
      for (const c of chunks) {
        await bulkUpdateStudents({
          organisationId: organisation.id,
          updates: c,
        }).unwrap();
        updated += c.length;
        toast.loading(`Updating passwords (${updated}/${allUpdates.length})`, {
          id: toastId,
        });
      }
      toast.success('Passwords updated', { id: toastId, description: '' });
    } catch (error) {
      console.error('Failed to update passwords', error);
      if (updated > 0) {
        toast.error('Failed to update passwords', {
          id: toastId,
          description: `Updated ${updated} of ${allUpdates.length}. Please retry.`,
        });
      } else {
        toast.error('Failed to update passwords', {
          id: toastId,
          description: UNKNOWN_ERROR_MESSAGE,
        });
      }
    } finally {
      setIsUpdatingPasswords(false);
    }
  }

  return (
    <GroupDetailsContainer>
      <Accordion
        type="multiple"
        defaultValue={['details', 'managers', 'settings']}
      >
        <AccordionItem value="details">
          <AccordionTrigger>
            <CaretDownIcon className="h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200" />
            Group Details
          </AccordionTrigger>

          <AccordionContent className="grid gap-3">
            <GroupDetailsRow>
              <GroupDetailLabel>
                <IdentificationCardIcon size={16} />
                Group ID
              </GroupDetailLabel>
              <span className="text-neutral-500 ml-2">{group.id}</span>
            </GroupDetailsRow>

            <GroupDetailsRow>
              <GroupDetailLabel>
                <MagicWandIcon size={16} />
                Name
              </GroupDetailLabel>
              <Input
                id="name"
                placeholder="Enter group name"
                value={updatedName}
                onChange={(e) => setUpdatedName(e.target.value)}
                onBlur={handleSave}
                onKeyUp={(e) => {
                  if (e.key === 'Enter') e.currentTarget.blur();
                }}
              />
            </GroupDetailsRow>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="managers">
          <AccordionTrigger>
            <CaretDownIcon className="h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200" />
            Managers ({managers.length})
          </AccordionTrigger>

          <AccordionContent>
            <GroupManagersList>
              {managers.map((manager) => (
                <GroupsManagerItem key={manager.uid}>
                  <TableInitialsCircle userName={manager.name} size={24} />
                  <span>{manager.name}</span>
                  <Button
                    variant="ghost"
                    className="px-2 ml-auto"
                    size="sm"
                    onClick={() => handleRemoveManager(manager.uid)}
                  >
                    <XIcon size={14} />
                  </Button>
                </GroupsManagerItem>
              ))}
              <Button
                variant="outline"
                className="flex gap-2 mt-4"
                size="sm"
                onClick={() => setManagerDialogOpen(true)}
              >
                <UsersThreeIcon size={18} />
                Edit Managers
              </Button>
            </GroupManagersList>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="settings">
          <AccordionTrigger>
            <CaretDownIcon className="h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200" />
            Game Settings
          </AccordionTrigger>
          <AccordionContent>
            {groupStudents.length === 0 ? (
              <p className="text-sm text-muted-foreground py-2">
                No learners in this group.
              </p>
            ) : (
              <div className="flex flex-col gap-4">
                {hasSettingsChanged && (
                  <Alert variant="warning" className="p-1.5">
                    <WarningCircleIcon size={16} weight="bold" />
                    <AlertDescription>
                      You have unsaved changes.
                    </AlertDescription>
                  </Alert>
                )}
                <p className="text-xs text-muted-foreground">
                  Changes will apply to all {groupStudents.length} learners in
                  this group.
                </p>
                <LearnerSettings
                  settings={settings}
                  onPlayPhonemeChanged={handlePlayPhonemeChanged}
                  onControllerTypeChange={handleControllerTypeChange}
                  onDuelsEnabledChange={handleDuelsEnabledChange}
                  onAudioDelayChange={handleAudioDelayChange}
                  onDistractorCardsChange={handleDistractorCardsChange}
                  onWordLimitChange={handleWordLimitChange}
                  onWordSegmentationChange={handleWordSegmentationChange}
                  onArticulatoryGesturesChange={
                    handleArticulatoryGesturesChange
                  }
                  largeLabels
                  disabled={isSettingsLoading}
                />
                {hasSettingsChanged && (
                  <Alert variant="warning" className="p-1.5">
                    <WarningCircleIcon size={16} weight="bold" />
                    <AlertDescription>
                      You have unsaved changes.
                    </AlertDescription>
                  </Alert>
                )}
                <div className="flex flex-col gap-2">
                  {canApplyOrgDefaults && (
                    <Button
                      variant="outline"
                      className="gap-1"
                      onClick={handleApplyOrgDefaults}
                      disabled={isSettingsLoading}
                    >
                      <ArrowCounterClockwiseIcon size={14} />
                      Apply organisation defaults
                    </Button>
                  )}
                  <Button
                    onClick={handleSaveSettings}
                    loading={isSettingsLoading}
                  >
                    Save Settings
                  </Button>
                </div>
              </div>
            )}
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="passwords" className="border-none">
          <AccordionTrigger>
            <CaretDownIcon className="h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200" />
            Update Passwords
          </AccordionTrigger>
          <AccordionContent>
            <div className="flex flex-col gap-4 p-1">
              <p className="text-xs text-muted-foreground">
                Changing password here will apply the same password to all{' '}
                {groupStudents.length} learners in this group.
              </p>
              <div className="relative">
                <Input
                  id="password"
                  type={passwordVisible ? 'text' : 'password'}
                  placeholder="New Password"
                  value={updatedPassword}
                  onChange={(e) => setUpdatedPassword(e.target.value)}
                />
                <div className="flex items-center gap-1 absolute right-1 top-1/2 -translate-y-1/2 bg-white">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        onClick={() =>
                          setUpdatedPassword(generateStudentPassword())
                        }
                      >
                        <ShuffleIcon className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Generate random password</TooltipContent>
                  </Tooltip>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        onClick={() => setPasswordVisible((prev) => !prev)}
                      >
                        {passwordVisible ? (
                          <EyeSlashIcon className="size-4" />
                        ) : (
                          <EyeIcon className="size-4" />
                        )}
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      {passwordVisible ? 'Hide' : 'Show'} Password
                    </TooltipContent>
                  </Tooltip>
                </div>
              </div>
              <Button
                onClick={handleUpdatePasswords}
                loading={isUpdatingPasswords}
                disabled={!updatedPassword}
              >
                Update Passwords
              </Button>
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>

      <Dialog open={isManagerDialogOpen} onOpenChange={setManagerDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Manager</DialogTitle>
            <DialogDescription>
              Select a user to manage this group
            </DialogDescription>
          </DialogHeader>

          <UserMultiSelect
            items={organisation?.members ?? []}
            selectedIds={updatedManagerIds}
            onSelect={handleManagerSelect}
            onFilter={handleFilterManagers}
            autoFocus={true}
            getItemId={(user) => user.uid}
            getItemName={(user) => user.name}
          />

          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline">Cancel</Button>
            </DialogClose>
            <DialogClose asChild>
              <Button
                onClick={handleUpdateManagers}
                disabled={isManagerDialogLoading}
              >
                {isManagerDialogLoading ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    Updating...
                  </>
                ) : (
                  'Update'
                )}
              </Button>
            </DialogClose>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </GroupDetailsContainer>
  );
}

const GroupDetailsContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0 16px;
`;

const GroupDetailsRow = styled.div`
  display: grid;
  grid-template-columns: 2fr 3fr;
  margin-right: 8px;
`;

const GroupDetailLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: hsl(var(--muted-foreground));
  font-size: 12px;
`;

const GroupManagersList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const GroupsManagerItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
`;
